// Apolo IDE — barra global de runs em segundo plano (auditoria 2026-07-02).
//
// Trocar de perfil com um agente rodando NÃO mata a run (ela continua no
// agentRunStore), mas a sessão sai da lista do perfil — ficava invisível e sem
// Stop. Esta barra flutuante mantém essas runs visíveis, com "Abrir" (volta pro
// perfil da run) e "Parar". Renderiza null quando não há run em segundo plano.

import { useMemo } from 'react';
import { Loader2, X, ArrowUpRight } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { useAuthStore } from '../../store/useAuthStore.js';
import { selectBackgroundRuns } from '../../lib/backgroundRuns.js';
import { getRunController, setRunning } from '../../lib/agentRunStore.js';
import { ABORT_REASON, abortWithReason } from '../../lib/loop/abortReason.js';

export default function BackgroundRunsBar() {
  const streamingSessionIds = useAgentStore((s) => s.code.streamingSessionIds);
  const sessions = useAgentStore((s) => s.code.sessions);
  const runMeta = useAgentStore((s) => s.code.runMeta);
  const setActiveSession = useAgentStore((s) => s.setActiveSession);
  const setActiveWorkspace = useAgentStore((s) => s.setActiveWorkspace);
  const refreshSessions = useAgentStore((s) => s.refreshSessions);
  const switchProfile = useAuthStore((s) => s.switchProfile);

  const background = useMemo(
    () => selectBackgroundRuns({ streamingSessionIds, sessions, runMeta }),
    [streamingSessionIds, sessions, runMeta],
  );

  if (!background.length) return null;

  async function openRun(run) {
    if (run.profileId) {
      try {
        await switchProfile(run.profileId);
        await refreshSessions();
      } catch { /* best-effort — segue e tenta abrir mesmo assim */ }
    }
    setActiveWorkspace('code');
    setActiveSession(run.sessionId);
  }

  function stopRun(run) {
    abortWithReason(getRunController(run.sessionId), ABORT_REASON.USER_STOP);
    setRunning(run.sessionId, false);
  }

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-30 flex flex-col items-center gap-2 px-4"
      role="status"
      aria-live="polite"
    >
      {background.map((run) => (
        <div
          key={run.sessionId}
          className="pointer-events-auto flex max-w-md items-center gap-3 rounded-full border border-apolo-borderSubtle bg-apolo-surface2/95 px-4 py-2 text-sm shadow-lg backdrop-blur"
        >
          <Loader2 size={14} className="shrink-0 animate-spin text-apolo-accent" aria-hidden="true" />
          <span className="min-w-0 truncate text-apolo-textSecondary">
            <span className="text-apolo-textPrimary">{run.name}</span>
            {' '}rodando em outro perfil
          </span>
          <button
            type="button"
            onClick={() => openRun(run)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-apolo-textSecondary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
            aria-label={`Abrir a sessão ${run.name}`}
          >
            <ArrowUpRight size={12} aria-hidden="true" /> Abrir
          </button>
          <button
            type="button"
            onClick={() => stopRun(run)}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-error focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
            aria-label={`Parar a sessão ${run.name}`}
            title="Parar"
          >
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}
